import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Pages opened from a Home card get a filter in `location.state`. They read it as initial state
 * on mount (so the first fetch is already filtered) and in an effect for later clicks while the
 * page stays open. Every navigation has its own `location.key`; the returned `claim(key)` is true
 * only the first time a key is seen, so each click applies its filter exactly once.
 *
 * @param appliedOnMount true when the current navigation's state was already used as initial state.
 */
export function useNavigationStateOnce(
  appliedOnMount: boolean,
): (navigationKey: string) => boolean {
  const location = useLocation();
  const appliedKeyRef = useRef<string | null>(appliedOnMount ? location.key : null);

  return useCallback((navigationKey: string) => {
    if (appliedKeyRef.current === navigationKey) {
      return false;
    }
    appliedKeyRef.current = navigationKey;
    return true;
  }, []);
}
